import { DeleteSquareIcon, PlusIcon } from '@/assets/icons';
import { OPTION_SELECT, PageTypeEnum } from '@/constant';
import { IAutocompleteSuggestionItem, TMappingDocumentItem } from '@/services';
import { CommonSelectors } from '@/stores';
import {
	FRANCHISE_EXCLUDED_DOCUMENT_TYPES,
	TDefineDocumentUploadListStore,
} from '@/stores/define-document-upload';
import { cn, filter, insertObjectIfElse, map } from '@/utils';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFormikContext } from 'formik';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';
import { AnyObject } from 'yup';

import { Select } from '..';
import { getRelativeOptionsByDocumentType } from '../define-document-area/define-document-area.config';

type RelationDocumentProps = {
	values: TDefineDocumentUploadListStore[0]['mappingList'];
	record: AnyObject;
	refTableId: number;
	subtractRelation: (id: number, record: AnyObject) => void;
	addRowRelation: (record: AnyObject, index?: number) => void;
	onChangeRelation: (value: string, record: AnyObject, id: number) => void;
	onChangeValue: (value: string, record: AnyObject, id: number) => void;
};

interface FormValues {
	documents: Array<{
		id: number;
		mappingList: Array<{
			id: number;
			refTableName?: string;
			refTableId?: string;
		}>;
	}>;
}

type MappingItem = {
	id: number;
	refTableName?: string;
	refTableId?: string;
};

const RelationDocument = ({
	values,
	record,
	refTableId,
	addRowRelation,
	subtractRelation,
	onChangeRelation,
	onChangeValue,
}: RelationDocumentProps) => {
	const {
		values: formikValues,
		errors,
		touched,
	} = useFormikContext<FormValues>();

	const autocompleteSuggestion = useSelector(
		CommonSelectors.selectAutocompleteSuggestion
	);

	const getAutocompleteOptions = useCallback(
		(refTableName?: PageTypeEnum) => {
			let list: IAutocompleteSuggestionItem[] = [];

			switch (refTableName) {
				case PageTypeEnum.FRANCHISE:
					list = autocompleteSuggestion?.franchises || [];
					break;

				case PageTypeEnum.APPLICANT:
					list = autocompleteSuggestion?.applicants || [];
					break;

				case PageTypeEnum.RECOMMENDER:
					list = autocompleteSuggestion?.recommenders || [];
					break;

				case PageTypeEnum.LODGING:
					list = autocompleteSuggestion?.lodgings || [];
					break;

				default:
					list = [];
			}

			list = list.filter((item) => {
				const isSameId = Number(item?.id) === Number(refTableId);
				const isSameTableName = record?.refTableName === refTableName;
				return !(isSameId && isSameTableName);
			});

			return map(list, (item) => ({
				value: item?.id,
				...insertObjectIfElse(
					refTableName === PageTypeEnum.LODGING,
					{
						label: `${item?.providerName ? `(${item?.providerName}) ` : ''}${item?.value}`,
					},
					{ label: item?.value }
				),
			}));
		},
		[autocompleteSuggestion, record, refTableId]
	);

	const valuesRelation = useMemo(() => {
		// if (isNilOrEmpty(record?.fileList)) return values;
		return filter(
			values,
			(item) =>
				item.refTableId !== record?.refTableId ||
				item.refTableName !== record?.refTableName
		);
	}, [values, record]);

	const getFilteredOptions = useCallback(
		(mappingItem: TMappingDocumentItem, index: number) => {
			const autocompleteOptions = getAutocompleteOptions(
				mappingItem?.refTableName
			);

			return autocompleteOptions.filter(
				(relation) =>
					!(
						relation.value === record?.fileList?.refTableId &&
						mappingItem?.refTableName === record?.fileList?.refTableName
					) &&
					!valuesRelation.some(
						(valueItem, valueIndex) =>
							valueIndex !== index && relation.value === valueItem.refTableId
					)
			);
		},
		[valuesRelation, record, getAutocompleteOptions]
	);

	const relatedInfoOptions = useMemo(
		() => [
			OPTION_SELECT,
			...getRelativeOptionsByDocumentType(record?.documentType),
		],
		[record?.documentType]
	);

	const isExcludedDocType = useMemo(() => {
		return (
			record?.refTableName === PageTypeEnum.FRANCHISE &&
			FRANCHISE_EXCLUDED_DOCUMENT_TYPES.includes(record?.documentType)
		);
	}, [record?.refTableName, record?.documentType]);

	// Determine if we can add more relation rows based on remaining unused types
	const canAddMore = useMemo(() => {
		const usedTypes = (record?.mappingList || [])
			.map((ml: TMappingDocumentItem) => ml.refTableName)
			.filter((v: any) => isNotNilOrEmpty(v));

		const usedTypesNormalized = isExcludedDocType
			? usedTypes.filter((t: any) => t !== PageTypeEnum.APPLICANT)
			: usedTypes;

		const remaining = relatedInfoOptions.filter((opt) => {
			if (isNilOrEmpty(opt?.value)) return false;
			if (opt?.value === record?.refTableName) {
				if (isExcludedDocType && opt?.value === PageTypeEnum.APPLICANT) {
					return true;
				}
				return false;
			}
			return !usedTypesNormalized.includes(opt?.value as any);
		});

		return remaining.length > 0;
	}, [
		record?.mappingList,
		record?.refTableName,
		relatedInfoOptions,
		isExcludedDocType,
	]);

	const indexRecord = useMemo(
		() => formikValues.documents.findIndex((item) => item.id === record.id),
		[formikValues.documents, record.id]
	);

	const mappingListInFormik = useMemo(
		() => formikValues.documents?.[indexRecord]?.mappingList || [],
		[formikValues.documents, indexRecord]
	);

	const isFieldTouched = useCallback(
		(field: keyof MappingItem, item: TMappingDocumentItem) => {
			const documentTouched = touched.documents?.[indexRecord];
			if (typeof documentTouched === 'string' || !documentTouched)
				return undefined;

			const mappingListTouched = documentTouched.mappingList;
			if (!Array.isArray(mappingListTouched)) return undefined;

			const indexMappingList = mappingListInFormik.findIndex(
				(itemMapping) => itemMapping.id === item.id
			);
			if (indexMappingList < 0) return undefined;

			const currentItemTouched = mappingListTouched[indexMappingList];
			if (typeof currentItemTouched === 'string' || !currentItemTouched)
				return undefined;

			return !!currentItemTouched[field];
		},
		[touched, indexRecord, mappingListInFormik]
	);

	const getFieldError = useCallback(
		(field: keyof MappingItem, item: TMappingDocumentItem) => {
			const documentErrors = errors.documents?.[indexRecord];
			if (typeof documentErrors === 'string' || !documentErrors)
				return undefined;

			const mappingListErrors = documentErrors.mappingList;
			if (!Array.isArray(mappingListErrors)) return undefined;

			const indexMappingList = mappingListInFormik.findIndex(
				(itemMapping) => itemMapping.id === item.id
			);
			if (indexMappingList < 0) return undefined;

			const currentItemErrors = mappingListErrors[indexMappingList];
			if (typeof currentItemErrors === 'string' || !currentItemErrors)
				return undefined;

			return currentItemErrors[field];
		},
		[errors, indexRecord, mappingListInFormik]
	);

	const renderRelationItem = useCallback(
		(item: TMappingDocumentItem, index: number) => {
			const _optionsRelation = getFilteredOptions(item, index);
			// Build options for refTableName: exclude types already used by other mapping items
			const usedTypes = (record?.mappingList || [])
				.filter((ml: TMappingDocumentItem) => ml.id !== item.id)
				.map((ml: TMappingDocumentItem) => ml.refTableName)
				.filter((v: any) => isNotNilOrEmpty(v));

			const usedTypesNormalized = isExcludedDocType
				? usedTypes.filter((t: any) => t !== PageTypeEnum.APPLICANT)
				: usedTypes;

			const refTableNameOptions = relatedInfoOptions.filter((opt) => {
				if (isNilOrEmpty(opt?.value) || opt?.value === item?.refTableName)
					return true;

				if (opt?.value === record?.refTableName) {
					if (isExcludedDocType && opt?.value === PageTypeEnum.APPLICANT) {
						return true;
					}
					return false;
				}
				return !usedTypesNormalized.includes(opt?.value as any);
			});

			return (
				<div className="flex justify-start items-center gap-2.5" key={item.id}>
					<div
						className={cn(
							'cursor-pointer',
							!canAddMore && 'opacity-50 pointer-events-none'
						)}
						onClick={() => canAddMore && addRowRelation(record, index)}
					>
						<PlusIcon />
					</div>

					<div className="flex gap-[5px] w-full">
						<Select
							options={refTableNameOptions}
							value={item?.refTableName}
							containerClassName="block grow-0"
							className="!w-[100px]"
							onChange={(value) => onChangeRelation(value, record, item.id)}
							touched={isFieldTouched('refTableName', item)}
							errorCode={getFieldError('refTableName', item)}
						/>

						<Select
							options={_optionsRelation}
							value={item?.refTableId}
							className={cn(
								'[&>div.ant-select-selector>.ant-select-selection-item]:!line-clamp-1 [&>div.ant-select-selector>.ant-select-selection-item]:!whitespace-normal'
							)}
							onChange={(value) => onChangeValue(value, record, item.id)}
							showSearch
							removeIcon
							suffixIcon
							touched={isFieldTouched('refTableId', item)}
							errorCode={getFieldError('refTableId', item)}
						/>
					</div>
					{valuesRelation.length > 1 && (
						<div
							className="cursor-pointer ml-1"
							onClick={() => subtractRelation(item.id, record)}
						>
							<DeleteSquareIcon width={12} height={12} />
						</div>
					)}
				</div>
			);
		},
		[
			valuesRelation,
			record,
			addRowRelation,
			subtractRelation,
			onChangeRelation,
			onChangeValue,
			getFilteredOptions,
			formikValues,
			errors,
			touched,
			isExcludedDocType,
			relatedInfoOptions,
			canAddMore,
			isFieldTouched,
			getFieldError,
		]
	);

	return (
		<div className="flex flex-col gap-1">
			{map(valuesRelation, renderRelationItem)}
		</div>
	);
};

export default memo(RelationDocument);
